import { useState, useEffect, useCallback } from 'react';

/** 추적 검색어 타입 */
export interface TrackedQuery {
  id: string;
  query: string;
  addedDate: string;
  priceHistory: { date: string; price: number; productTitle: string }[];
  currentLowest: number;
  currentProductTitle: string;
}

const STORAGE_KEY = 'tracked_queries';
const API_BASE = process.env.REACT_APP_API_URL || '';
const MAX_TRACKED = 20;

function loadTracked(): TrackedQuery[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveTracked(items: TrackedQuery[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

function today(): string {
  return new Date().toISOString().split('T')[0];
}

/** 검색어 기반 최저가 추적 훅 */
export function usePriceTracker() {
  const [tracked, setTracked] = useState<TrackedQuery[]>(loadTracked);

  /* localStorage 동기화 */
  useEffect(() => { saveTracked(tracked); }, [tracked]);

  /* KV 동기화: tracked 변경 시 서버에 전송 */
  useEffect(() => {
    if (tracked.length === 0) return;

    const syncToKV = async () => {
      try {
        await fetch(`${API_BASE}/api/tracker/sync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            queries: tracked.map((q) => ({
              id: q.id,
              query: q.query,
              addedDate: q.addedDate,
            })),
          }),
        });
      } catch {
        console.warn('[PriceTracker] KV 동기화 실패');
      }
    };

    syncToKV();
  }, [tracked]);

  /** 검색어 추적 등록 */
  const addQuery = useCallback((query: string) => {
    setTracked((prev) => {
      if (prev.some((q) => q.query === query)) return prev;
      if (prev.length >= MAX_TRACKED) return prev;
      const newItem: TrackedQuery = {
        id: Date.now().toString(),
        query,
        addedDate: today(),
        priceHistory: [],
        currentLowest: 0,
        currentProductTitle: '',
      };
      return [...prev, newItem];
    });
  }, []);

  /** 추적 삭제 */
  const removeQuery = useCallback((id: string) => {
    setTracked((prev) => prev.filter((q) => q.id !== id));
  }, []);

  /** 검색어가 이미 추적 중인지 확인 */
  const isQueryTracked = useCallback((query: string): boolean => {
    return tracked.some((q) => q.query === query);
  }, [tracked]);

  /** 추적 중인 검색어들의 현재 최저가를 서버에서 조회하여 업데이트 */
  const refreshPrices = useCallback(async () => {
    if (tracked.length === 0) return;

    try {
      /* 1) 네이버 API로 현재 최저가 조회 */
      const res = await fetch(`${API_BASE}/api/tracker/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          queries: tracked.map((q) => ({ id: q.id, query: q.query })),
        }),
      });

      if (!res.ok) return;

      const { prices } = await res.json() as {
        prices: Record<string, { price: number; productTitle: string }>;
      };

      /* 2) KV에서 Cron Worker가 수집한 가격 이력 가져오기 */
      let kvHistory: Record<string, { date: string; price: number; productTitle: string }[]> = {};
      try {
        const histRes = await fetch(`${API_BASE}/api/tracker/history`);
        if (histRes.ok) {
          const histData = await histRes.json() as {
            data: Record<string, { date: string; price: number; productTitle: string }[]>;
          };
          kvHistory = histData.data || {};
        }
      } catch {
        console.warn('[PriceTracker] KV 이력 조회 실패');
      }

      /* 3) 현재 가격 + KV 이력을 로컬 이력에 병합 */
      const todayStr = today();
      setTracked((prev) => prev.map((q) => {
        let merged = [...q.priceHistory];

        // KV 이력 병합 (날짜 기준 중복 제거)
        const kvEntries = kvHistory[q.id] || [];
        const existingDates = new Set(merged.map((e) => e.date));
        for (const entry of kvEntries) {
          if (!existingDates.has(entry.date)) {
            merged.push(entry);
          }
        }

        merged.sort((a, b) => a.date.localeCompare(b.date));

        // 오늘 최저가 업데이트
        const priceInfo = prices[q.id];
        if (priceInfo && priceInfo.price > 0) {
          const last = merged[merged.length - 1];
          const entry = { date: todayStr, price: priceInfo.price, productTitle: priceInfo.productTitle };
          if (last?.date === todayStr) {
            merged[merged.length - 1] = entry;
          } else {
            merged.push(entry);
          }
        }

        const currentLowest = priceInfo?.price ?? q.currentLowest;
        const currentProductTitle = priceInfo?.productTitle ?? q.currentProductTitle;
        return { ...q, priceHistory: merged, currentLowest, currentProductTitle };
      }));
    } catch {
      console.warn('[PriceTracker] 가격 새로고침 실패');
    }
  }, [tracked]);

  return { tracked, addQuery, removeQuery, isQueryTracked, refreshPrices };
}
